import { formatCurrency } from '@ps/web-poker-utils';
import type { MessageDescriptor } from 'react-intl';

import i18n from '../../i18n';
import type { Options, PokerGameTileAttribute } from '../../types';
import { formatTimeDisplay } from '../../utils/timeDisplay';
import { BASE_POKER_STR, ICON_IMAGE_FORMAT, THE_RAIL_CDN_BASE_URL } from '../game';
import { AttributeKey } from '../types';

export interface MttAttributes {
  buyIn: number;
  rake: number;
  maxPlayers: number;
  currency: number | string;
  isPlayMoney: number;
  scalePM: number;
  prizePoolToPublish: number;
  prizePoolUnit: number;
  guaranteedPrizePool: number;
  whenStartInt: number;
  regStartInt?: number;
  regEndAbsInt: number;
  minPlayers: number;
  whenStart: string;
  fakeStatus: number;
  winnerAward?: number;
}

interface CommonAttributes {
  name: string;
  key: number;
  prizePool: number;
  currency: string | number;
  isPlayMoney: number;
  scalePM: number;
  prizePoolToPublish: number;
  i18nInstance: (id: MessageDescriptor['id']) => string;
  prizePoolUnit: number;
  winnerAward: number;
  shortPrizePool?: boolean;
}

enum PrizePoolUnits {
  Seat = 4,
  SeatPlus = 132,
  Ticket = 5,
  TicketPlus = 133,
  Package = 6,
  PackagePlus = 134
}

// @note: prizePoolUnit value comes as unsigned integer from a server
// @see https://confluence.pyrsoftware.ca/confluence/display/~szhizhin/How+PrizePoolUnit+works
const limitPrizePoolUnitToByte = (prizePoolUnit: number) => prizePoolUnit & 0xff; // eslint-disable-line no-bitwise

const isPrizePoolSeat = (prizePoolUnit: number) =>
  prizePoolUnit === PrizePoolUnits.Seat || prizePoolUnit === PrizePoolUnits.SeatPlus;

const isPrizePoolTicket = (prizePoolUnit: number) =>
  prizePoolUnit === PrizePoolUnits.Ticket || prizePoolUnit === PrizePoolUnits.TicketPlus;

const isPrizePoolPackage = (prizePoolUnit: number) =>
  prizePoolUnit === PrizePoolUnits.Package || prizePoolUnit === PrizePoolUnits.PackagePlus;

export const isPrizePoolRealMoney = (prizePoolUnit: number) => {
  const prizeUnitLimited = limitPrizePoolUnitToByte(prizePoolUnit);

  return (
    !isPrizePoolSeat(prizeUnitLimited) &&
    !isPrizePoolTicket(prizeUnitLimited) &&
    !isPrizePoolPackage(prizeUnitLimited)
  );
};

const isPrizePoolPlus = (prizePoolUnit: number) =>
  prizePoolUnit === PrizePoolUnits.SeatPlus ||
  prizePoolUnit === PrizePoolUnits.TicketPlus ||
  prizePoolUnit === PrizePoolUnits.PackagePlus;

const getPrizePoolLabels = ({
  prizePoolToPublish,
  i18nInstance,
  prizePoolUnit
}: Pick<CommonAttributes, 'prizePoolToPublish' | 'i18nInstance' | 'prizePoolUnit'>) => {
  let prizeLabel = `${prizePoolToPublish} `;

  if (isPrizePoolSeat(prizePoolUnit)) {
    prizeLabel += i18nInstance(prizePoolToPublish > 1 ? 'TXTWBCLI_Seats' : 'TXTWBCLI_Seat');
  } else if (isPrizePoolTicket(prizePoolUnit)) {
    prizeLabel += prizePoolToPublish > 1 ? 'tickets' : 'ticket';
  } else if (isPrizePoolPackage(prizePoolUnit)) {
    prizeLabel += prizePoolToPublish > 1 ? 'packages' : 'package';
  }

  if (isPrizePoolPlus(prizePoolUnit)) {
    prizeLabel += '+';
  }

  return prizeLabel;
};

const getPrizePool = ({
  guaranteedPrizePool,
  prizePoolToPublish
}: Pick<MttAttributes, 'guaranteedPrizePool' | 'prizePoolToPublish'>) => {
  if (guaranteedPrizePool) return guaranteedPrizePool;
  if (prizePoolToPublish) return prizePoolToPublish;
  // WEB-9435 - when no players are registered in a tournament we should not
  // calculate a minimum as it may be misleading
  return 0;
};

interface PrizePoolAttrExtra {
  moneyIcon?: string;
}

const getFormattedPrize = ({
  name,
  key,
  prizePoolUnit,
  prizePool,
  prizePoolToPublish,
  currency,
  isPlayMoney,
  scalePM,
  i18nInstance,
  shortPrizePool = false
}: Omit<CommonAttributes, 'winnerAward'>) => {
  const prizePoolUnitLimited = limitPrizePoolUnitToByte(prizePoolUnit);
  const extra: PrizePoolAttrExtra = {};

  if (isPrizePoolPlus(prizePoolUnitLimited)) {
    extra.moneyIcon = `${THE_RAIL_CDN_BASE_URL}${BASE_POKER_STR}icon_money@2x${ICON_IMAGE_FORMAT}`;
  }

  return {
    ...extra,
    name,
    value:
      isPrizePoolSeat(prizePoolUnitLimited) ||
      isPrizePoolTicket(prizePoolUnitLimited) ||
      isPrizePoolPackage(prizePoolUnitLimited)
        ? getPrizePoolLabels({
            prizePoolToPublish,
            i18nInstance,
            prizePoolUnit: prizePoolUnitLimited
          })
        : formatCurrency(prizePool, currency, {
            separators: true,
            isPlayMoney,
            scalePM,
            shortPrizePool
          }),
    key
  };
};

const getWinnerAwardAttribute = ({
  name,
  prizePoolUnit,
  winnerAward,
  currency,
  isPlayMoney,
  scalePM,
  i18nInstance
}: Omit<CommonAttributes, 'key' | 'prizePoolToPublish' | 'prizePool' | 'shortPrizePool'>) =>
  getFormattedPrize({
    name,
    prizePoolUnit,
    prizePool: winnerAward,
    prizePoolToPublish: winnerAward,
    currency,
    isPlayMoney,
    scalePM,
    i18nInstance,
    key: AttributeKey.winnerAward
  });

const getPrizePoolAttribute = ({
  name,
  key,
  prizePoolUnit,
  prizePoolToPublish,
  prizePool,
  currency,
  isPlayMoney,
  scalePM,
  i18nInstance,
  shortPrizePool = false
}: Omit<CommonAttributes, 'winnerAward'>) =>
  getFormattedPrize({
    name,
    prizePoolUnit,
    prizePool,
    prizePoolToPublish,
    currency,
    isPlayMoney,
    scalePM,
    i18nInstance,
    shortPrizePool,
    key
  });

export default function mttAttrFormatter(
  {
    buyIn,
    rake,
    maxPlayers,
    currency,
    isPlayMoney,
    prizePoolToPublish,
    prizePoolUnit,
    minPlayers,
    guaranteedPrizePool,
    whenStartInt,
    regEndAbsInt,
    scalePM,
    whenStart,
    fakeStatus,
    winnerAward
  }: MttAttributes,
  { language, timeZone }: Options
) {
  const i18nInstance = i18n(language);
  const prizePool = getPrizePool({ guaranteedPrizePool, prizePoolToPublish });
  const attributes: PokerGameTileAttribute[] = [
    {
      name: i18nInstance('TXTWBCLI_Buy_In'),
      value: buyIn
        ? formatCurrency(buyIn + rake, currency, {
            separators: true,
            isPlayMoney,
            scalePM
          })
        : i18nInstance('TXTWBCLI_Freeroll'),
      key: AttributeKey.buyIn
    }
  ];

  // On-Demand SnG - AQA-31681
  //
  // Change AQA-31681 implementation as per PYR-164152 request:
  // prize pool label should be displayed before tournament start

  if (!whenStart && minPlayers !== maxPlayers) {
    attributes.push({
      name: i18nInstance('TXTWBCLI_Players'),
      value: `${minPlayers}-${maxPlayers}`,
      key: AttributeKey.players
    });
  }

  attributes.push(
    getPrizePoolAttribute({
      name: i18nInstance('TXTWBCLI_Prize_Pool'),
      key: AttributeKey.prizePool,
      prizePoolUnit,
      prizePoolToPublish,
      prizePool,
      currency,
      isPlayMoney,
      scalePM,
      i18nInstance
    })
  );

  // shortPrizePool attribute
  attributes.push(
    getPrizePoolAttribute({
      name: i18nInstance('TXTWBCLI_Prize_Pool'),
      key: AttributeKey.shortPrizePool,
      prizePoolUnit,
      prizePoolToPublish,
      prizePool,
      currency,
      isPlayMoney,
      scalePM,
      i18nInstance,
      shortPrizePool: true
    })
  );

  if (winnerAward !== null && winnerAward !== undefined) {
    attributes.push(
      getWinnerAwardAttribute({
        name: 'First prize',
        prizePoolUnit,
        winnerAward,
        currency,
        isPlayMoney,
        scalePM,
        i18nInstance
      })
    );
  }

  const timeDisplayAttribute = formatTimeDisplay(
    whenStartInt,
    regEndAbsInt,
    timeZone,
    i18nInstance,
    fakeStatus,
    language
  );

  if (timeDisplayAttribute) {
    attributes.push(timeDisplayAttribute);
  }

  return attributes;
}
